import { useState, useEffect, useRef, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Button } from 'components/atoms/Button';
import TabsToggle from 'components/molecules/TabsToggle.component';
import useGetCardsList from 'hooks/Bank/useGetCardsList';
import useQueryParams from 'hooks/shared/useQueryParams';
import { useIntercom } from 'hooks/intercom.hook';
import { getCardSensitiveData } from 'api/v2/connectors/getCardSensitiveData';
import { createActivationResumptionURL } from 'hooks/Bank/useActivationFlowResumption';
import type { ActivationStepProps } from '../../types';
import OnboardingStepWrapper from '../../../components/OnboardingStepWrapper';

// Platform-specific wallet icons
import applePayIcon from './images/apple_pay.webp';
import googlePayIcon from './images/google_pay.webp';

type WalletType = 'apple' | 'google';

/**
 * Step showing instructions to add card to Apple Pay/Google Pay
 * Features:
 * - Tabs to switch between Apple Pay and Google Pay
 * - Platform-specific instructions and icons
 * - Full integration with card details display
 */
const CardToWalletStep: FC<ActivationStepProps> = ({
  onContinue,
  onBack,
  // Wrapper props (automatically injected)
  progressSegments,
  onProgressSegmentClick,
  showProgressBar,
  isUpdatingStep,
}) => {
  const { t } = useTranslation();
  const { showIntercom } = useIntercom();
  const [selectedWallet, setSelectedWallet] = useState<WalletType>('apple');
  const { data: cards } = useGetCardsList();
  const queryParams = useQueryParams();
  const hasAutoProgressed = useRef(false);

  // Find the enabled card for showing sensitive data
  const enabledCard = cards?.find((card) => card.status === 'Enabled');

  // Mutation for viewing card sensitive data (redirects to Swan and back)
  const viewCardDataMutation = useMutation(
    () => {
      const successUri = createActivationResumptionURL(
        'card_to_wallet', // Return to this same step after Swan
        { swanSuccess: 'true' }, // Mark as successful Swan return
      );
      const errorUri = createActivationResumptionURL('card_to_wallet', {
        cardViewError: 'true',
      });

      return getCardSensitiveData(enabledCard?.cardId, {
        successRedirectUri: successUri,
        errorRedirectUri: errorUri,
        type: 'virtual',
      });
    },
    {
      onSuccess: ({ redirectUri }) => {
        // Redirect to Swan in same window (will return via resumeActivation query param)
        window.location.href = redirectUri;
      },
    },
  );

  /**
   * Auto-progress to next step after successful Swan return
   * When user returns from Swan with swanSuccess=true, automatically
   * call onContinue to update backend and move to next step
   * Uses ref to ensure this only happens once, even if effect re-runs
   */
  useEffect(() => {
    if (queryParams.swanSuccess === 'true' && !hasAutoProgressed.current) {
      hasAutoProgressed.current = true;
      onContinue();
    }
  }, [queryParams.swanSuccess, onContinue]);

  const handleShowCardDetails = () => {
    if (enabledCard) {
      viewCardDataMutation.mutate();
    }
  };

  // Tab configuration
  const tabs = [
    {
      label: 'Apple Pay',
      value: 'apple' as WalletType,
    },
    {
      label: 'Google Pay',
      value: 'google' as WalletType,
    },
  ];
  const walletContent = {
    apple: {
      title: t('accountable_banking.activation.card_to_wallet.ios.title'),
      subtitle: t('accountable_banking.activation.card_to_wallet.ios.subtitle'),
      icon: applePayIcon,
      steps: [
        t('accountable_banking.activation.card_to_wallet.step_1.ios'),
        t('accountable_banking.activation.card_to_wallet.step_2.ios'),
        t('accountable_banking.activation.card_to_wallet.step_3.ios'),
        t('accountable_banking.activation.card_to_wallet.step_4.ios'),
        t('accountable_banking.activation.card_to_wallet.step_5.ios'),
        t('accountable_banking.activation.card_to_wallet.step_6.ios'),
      ],
      helpText: t(
        'accountable_banking.activation.card_to_wallet.help_text.ios',
      ),
    },
    google: {
      title: t('accountable_banking.activation.card_to_wallet.android.title'),
      subtitle: t(
        'accountable_banking.activation.card_to_wallet.android.subtitle',
      ),
      icon: googlePayIcon,
      steps: [
        t('accountable_banking.activation.card_to_wallet.step_1.android'),
        t('accountable_banking.activation.card_to_wallet.step_2.android'),
        t('accountable_banking.activation.card_to_wallet.step_3.android'),
        t('accountable_banking.activation.card_to_wallet.step_4.android'),
        t('accountable_banking.activation.card_to_wallet.step_5.android'),
        t('accountable_banking.activation.card_to_wallet.step_6.android'),
      ],
      helpText: t(
        'accountable_banking.activation.card_to_wallet.help_text.android',
      ),
    },
  };

  const currentContent = walletContent[selectedWallet];

  return (
    <OnboardingStepWrapper
      showProgressBar={showProgressBar}
      progressSegments={progressSegments}
      onProgressSegmentClick={onProgressSegmentClick}
      showBackButton
      onBack={onBack}
    >
      {/* Tabs - Below progress bar, above title/content */}
      <div className="mb-6">
        <TabsToggle
          tabs={tabs}
          selectedTab={selectedWallet}
          setSelectedTab={(tab) => setSelectedWallet(tab as WalletType)}
        />
      </div>

      {/* Platform-specific icon */}
      <div className="flex justify-center mb-4">
        <img
          src={currentContent.icon}
          alt={currentContent.title}
          className="h-10 w-20 object-contain"
        />
      </div>

      {/* Title and Subtitle */}
      <div className="mb-5">
        <h2 className="font-degular text-2xl font-bold text-center mb-2">
          {currentContent.title}
        </h2>
        <p className="text-primary-600 text-center max-w-lg mx-auto">
          {currentContent.subtitle}
        </p>
      </div>

      {/* List */}
      <ol className="space-y-2 text-sm text-black mb-5">
        {currentContent.steps.map((step, index) => (
          <li key={index} className="flex gap-3">
            <span className="font-semibold flex-shrink-0">{index + 1}.</span>
            <span>{step}</span>
          </li>
        ))}
      </ol>

      {/* Help text */}
      <p className="text-sm text-gray-600 mb-6">
        {currentContent.helpText}{' '}
        <button
          onClick={showIntercom}
          className="text-primary-600 underline capitalize"
        >
          {t('contact_us')}
        </button>
        <span>
          {' '}
          {t('accountable_banking.activation.card_to_wallet.contact_us')}
        </span>
      </p>

      {/* Actions */}
      <div className="flex flex-col gap-3">
        {enabledCard && (
          <Button
            onClick={handleShowCardDetails}
            className="w-full"
            color="bank"
            disabled={viewCardDataMutation.isLoading}
          >
            {viewCardDataMutation.isLoading
              ? t('loading')
              : t('show_card_details')}
          </Button>
        )}
        <Button
          onClick={onContinue}
          structure="text"
          className="w-full"
          disabled={isUpdatingStep}
        >
          {t('skip_for_now')}
        </Button>
      </div>
    </OnboardingStepWrapper>
  );
};

export default CardToWalletStep;
